import { TableRect, selectedRect, CellSelection } from 'prosemirror-tables';
import { ResolvedPos, Node as ProsemirrorNode, Schema, Slice, Mark } from 'prosemirror-model';
import { Transaction, TextSelection, EditorState } from 'prosemirror-state';
import { NodeWithPos, findChildren, Editor } from '@tiptap/vue-2';
import { EditorHelper } from '@lib-shared/editor/helpers/editor.helper';
import { IAlignList, ISetHeading, ISetLink, ISetParagraph } from '@/editor/editor-block/types';
import { EProsemirrorType } from '@lib-shared/editor/types/enums';

export class ProsemirrorHelper {
  static selectTextNode(transaction: Transaction, position: number): Transaction {
    return transaction.setSelection(TextSelection.create(transaction.doc, position));
  }

  static selectTextNearPosition(transaction: Transaction, position: number): Transaction {
    const resolvedPosition = transaction.doc.resolve(position);
    return transaction.setSelection(TextSelection.near(resolvedPosition));
  }

  static deleteNode(transaction: Transaction, position: number, nodeSize: number): Transaction {
    return transaction.delete(position, position + nodeSize);
  }

  static createEditorStateFromSlice(slice: Slice, schema: Schema): EditorState {
    const state: EditorState = EditorState.create({ schema });
    const transaction: Transaction = state.tr.replace(0, state.doc.content.size, slice);
    return state.apply(transaction);
  }

  static isCompleteTableSelected(state: EditorState): boolean {
    const { left, right, map, top, bottom }: TableRect = selectedRect(state);
    return left === 0 && right === map.width && top === 0 && map.height === bottom;
  }

  static getNodeAttributesFromPosition(
    transaction: Transaction,
    position: number
  ): ProsemirrorNode['attrs'] | undefined {
    const node: ProsemirrorNode = transaction.doc.nodeAt(position);

    if (!node) {
      return;
    }

    return node.attrs;
  }

  static getNodeBeforePosition(transaction: Transaction, position: number): ProsemirrorNode {
    const resolvedPosition: ResolvedPos = transaction.doc.resolve(position);
    return resolvedPosition.nodeBefore;
  }

  static getNodeAfterPosition(transaction: Transaction, position: number): ProsemirrorNode {
    const resolvedPosition: ResolvedPos = transaction.doc.resolve(position);
    return resolvedPosition.nodeAfter;
  }

  static updateNodeAttributes(
    transaction: Transaction,
    schema: Schema,
    position: number,
    nodeType: EProsemirrorType,
    attributes: ProsemirrorNode['attrs']
  ): Transaction | undefined {
    const attrs: ProsemirrorNode['attrs'] = ProsemirrorHelper.getNodeAttributesFromPosition(
      transaction,
      position
    );

    if (!attrs) {
      return;
    }

    return transaction.setNodeMarkup(position, schema.nodes[nodeType], {
      ...attrs,
      ...attributes,
    });
  }

  static alignList(
    { state, view: { dispatch } }: Editor,
    { align, position }: IAlignList,
    type: EProsemirrorType
  ) {
    const { tr, schema, doc } = state;
    const listNode: ProsemirrorNode = doc.nodeAt(position);

    if (!listNode) {
      return;
    }

    const nodes: NodeWithPos[] = findChildren(listNode, (node: ProsemirrorNode) =>
      [EProsemirrorType.Paragraph, EProsemirrorType.Heading].includes(
        node.type.name as EProsemirrorType
      )
    );

    nodes.forEach(({ pos, node }: NodeWithPos) => {
      const nodePosition = position + pos + 1;
      const nodeAttributes = ProsemirrorHelper.getNodeAttributesFromPosition(tr, nodePosition);

      tr.setNodeMarkup(nodePosition, schema.nodes[node.type.name], { ...nodeAttributes, align });
    });

    dispatch(
      tr.setNodeMarkup(position, schema.nodes[type], {
        align,
      })
    );
  }

  static getLinkStartEndPos(state: EditorState, pos: number): { from: number; to: number } {
    const $pos = state.doc.resolve(pos);

    const { parent, parentOffset } = $pos;
    const start = parent.childAfter(parentOffset);
    if (!start.node) {
      return null;
    }

    const link = start.node.marks.find(mark => mark.type === state.schema.marks.link);
    if (!link) {
      return null;
    }

    let startIndex = $pos.index();
    let startPos = $pos.start() + start.offset;
    let endIndex = startIndex + 1;
    let endPos = startPos + start.node.nodeSize;
    while (startIndex > 0 && link.isInSet(parent.child(startIndex - 1).marks)) {
      startIndex -= 1;
      startPos -= parent.child(startIndex).nodeSize;
    }
    while (endIndex < parent.childCount && link.isInSet(parent.child(endIndex).marks)) {
      endPos += parent.child(endIndex).nodeSize;
      endIndex += 1;
    }
    return { from: startPos, to: endPos };
  }

  static getTagIds(content = ''): string[] {
    if (!content) {
      return [];
    }
    return content
      .split('"tagId":"')
      .slice(1)
      .map(id => id.substring(0, EditorHelper.NANO_ID_LENGTH));
  }

  static replaceMergeNodes(fromTransaction: Transaction, schema: Schema): Transaction {
    let transaction = fromTransaction;

    const nodes: NodeWithPos[] = findChildren(
      transaction.doc,
      (node: ProsemirrorNode) => node.type.name === EProsemirrorType.MergeTag
    ).reverse();

    nodes.forEach(({ node, pos }: NodeWithPos) => {
      const mergeId = node.attrs.tagId;

      if (mergeId) {
        transaction = transaction.replaceWith(
          pos,
          pos + node.nodeSize,
          schema.text(`{{${mergeId}}}`, node.marks)
        );
      }
    });

    return transaction;
  }

  static isCellSelection(value: unknown): value is CellSelection {
    return value instanceof CellSelection;
  }

  static setTextType(
    { state: { doc, tr, schema }, view: { dispatch } }: Editor,
    payload: ISetParagraph | ISetHeading,
    type: EProsemirrorType
  ) {
    const { position, ...attributes } = payload;

    const node: ProsemirrorNode = doc.nodeAt(position);

    if (!node) {
      return;
    }

    const transaction: Transaction = tr.setBlockType(
      position,
      position + node.nodeSize,
      schema.nodes[type],
      {
        ...attributes,
      }
    );

    dispatch(transaction);
  }

  static aligntText(
    { state: { tr, schema }, view: { dispatch } }: Editor,
    payload: ISetParagraph | ISetHeading,
    type: EProsemirrorType
  ) {
    const { position, ...attributes } = payload;

    const transaction: Transaction = ProsemirrorHelper.updateNodeAttributes(
      tr,
      schema,
      position,
      type,
      { ...attributes }
    );

    dispatch(transaction);
  }

  static setLinkWithoutSelection(
    { state: { doc, selection, schema, tr }, view: { dispatch } }: Editor,
    payload: ISetLink
  ): boolean {
    const selectedNode: ProsemirrorNode = doc.nodeAt(selection.anchor);
    const hasLink = !!selectedNode?.marks.find((mark: Mark) => mark.attrs['href']);

    if (selection.empty && !hasLink) {
      const { href: linkText } = payload;
      const node: ProsemirrorNode = schema.text(linkText, [
        ...(selectedNode?.marks || []),
        schema.marks.link.create(payload),
      ]);

      dispatch(tr.replaceSelectionWith(node, false));

      return true;
    } else {
      return false;
    }
  }
}
